(function () {
  if (window.ProductAutoTrimInitialized) return;
  window.ProductAutoTrimInitialized = true;

  const CONFIG = {
    outputSize: 1200,
    analysisMaxSize: 700,
    padding: 0.05,
    colorTolerance: 22,
    alphaThreshold: 10,
    minimumWhitespaceRatio: 0.03,
    minimumBackgroundChannel: 205,
    debug: false
  };

  const processedImages = new Map();
  const queuedImages = new WeakSet();
  const workQueue = [];
  let workQueueRunning = false;
  const lazyImageObserver = 'IntersectionObserver' in window
    ? new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          lazyImageObserver.unobserve(entry.target);
          unveilLazyImage(entry.target);
        });
      }, { rootMargin: '500px 0px' })
    : null;

  function log() {
    if (!CONFIG.debug) return;
    const args = Array.prototype.slice.call(arguments);
    args.unshift('[AUTO-TRIM]');
    console.log.apply(console, args);
  }

  function schedule(callback) {
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(callback, { timeout: 1200 });
    } else {
      window.setTimeout(callback, 16);
    }
  }

  function loadImage(url) {
    return new Promise(function (resolve, reject) {
      const image = new Image();
      image.crossOrigin = 'anonymous';
      image.onload = function () { resolve(image); };
      image.onerror = reject;
      image.src = url;
    });
  }

  function getBackgroundColor(data, width, height) {
    const sampleSize = Math.max(2, Math.floor(Math.min(width, height) * 0.01));
    const areas = [
      [0, 0],
      [width - sampleSize, 0],
      [0, height - sampleSize],
      [width - sampleSize, height - sampleSize]
    ];
    let red = 0;
    let green = 0;
    let blue = 0;
    let count = 0;

    areas.forEach(function (area) {
      for (let y = area[1]; y < area[1] + sampleSize; y += 1) {
        for (let x = area[0]; x < area[0] + sampleSize; x += 1) {
          const index = (y * width + x) * 4;
          if (data[index + 3] <= CONFIG.alphaThreshold) continue;
          red += data[index];
          green += data[index + 1];
          blue += data[index + 2];
          count += 1;
        }
      }
    });

    if (!count) return { red: 255, green: 255, blue: 255 };
    return {
      red: red / count,
      green: green / count,
      blue: blue / count
    };
  }

  function findContentBounds(imageData) {
    const data = imageData.data;
    const width = imageData.width;
    const height = imageData.height;
    const background = getBackgroundColor(data, width, height);

    if (
      background.red < CONFIG.minimumBackgroundChannel ||
      background.green < CONFIG.minimumBackgroundChannel ||
      background.blue < CONFIG.minimumBackgroundChannel
    ) {
      log('Background is not light enough', background);
      return null;
    }

    const toleranceSquared = CONFIG.colorTolerance * CONFIG.colorTolerance;
    let minX = width;
    let minY = height;
    let maxX = -1;
    let maxY = -1;

    for (let y = 0; y < height; y += 1) {
      for (let x = 0; x < width; x += 1) {
        const index = (y * width + x) * 4;
        if (data[index + 3] <= CONFIG.alphaThreshold) continue;
        const redDiff = data[index] - background.red;
        const greenDiff = data[index + 1] - background.green;
        const blueDiff = data[index + 2] - background.blue;
        const distanceSquared =
          redDiff * redDiff + greenDiff * greenDiff + blueDiff * blueDiff;

        if (distanceSquared <= toleranceSquared) continue;
        if (x < minX) minX = x;
        if (y < minY) minY = y;
        if (x > maxX) maxX = x;
        if (y > maxY) maxY = y;
      }
    }

    if (maxX < minX || maxY < minY) return null;
    return {
      x: minX,
      y: minY,
      width: maxX - minX + 1,
      height: maxY - minY + 1
    };
  }

  function canvasToBlob(canvas) {
    return new Promise(function (resolve) {
      canvas.toBlob(resolve, 'image/jpeg', 0.92);
    });
  }

  async function createTrimmedUrl(originalUrl) {
    const image = await loadImage(originalUrl);
    const sourceWidth = image.naturalWidth || image.width;
    const sourceHeight = image.naturalHeight || image.height;
    if (!sourceWidth || !sourceHeight) return null;

    const analysisScale = Math.min(
      1,
      CONFIG.analysisMaxSize / Math.max(sourceWidth, sourceHeight)
    );
    const analysisCanvas = document.createElement('canvas');
    analysisCanvas.width = Math.max(1, Math.round(sourceWidth * analysisScale));
    analysisCanvas.height = Math.max(1, Math.round(sourceHeight * analysisScale));
    const analysisContext = analysisCanvas.getContext('2d', { willReadFrequently: true });
    if (!analysisContext) return null;
    analysisContext.drawImage(image, 0, 0, analysisCanvas.width, analysisCanvas.height);

    const imageData = analysisContext.getImageData(
      0,
      0,
      analysisCanvas.width,
      analysisCanvas.height
    );
    const bounds = findContentBounds(imageData);
    if (!bounds) return null;

    const whitespaceRatio = 1 - (
      bounds.width * bounds.height /
      (analysisCanvas.width * analysisCanvas.height)
    );
    if (whitespaceRatio < CONFIG.minimumWhitespaceRatio) return null;

    const scaleX = sourceWidth / analysisCanvas.width;
    const scaleY = sourceHeight / analysisCanvas.height;
    const sourceBounds = {
      x: Math.max(0, Math.floor(bounds.x * scaleX)),
      y: Math.max(0, Math.floor(bounds.y * scaleY)),
      width: Math.min(sourceWidth, Math.ceil(bounds.width * scaleX)),
      height: Math.min(sourceHeight, Math.ceil(bounds.height * scaleY))
    };
    const outputCanvas = document.createElement('canvas');
    outputCanvas.width = CONFIG.outputSize;
    outputCanvas.height = CONFIG.outputSize;
    const outputContext = outputCanvas.getContext('2d');
    if (!outputContext) return null;
    outputContext.fillStyle = '#ffffff';
    outputContext.fillRect(0, 0, CONFIG.outputSize, CONFIG.outputSize);

    const availableSize = CONFIG.outputSize * (1 - CONFIG.padding * 2);
    const outputScale = Math.min(
      availableSize / sourceBounds.width,
      availableSize / sourceBounds.height
    );
    const drawWidth = sourceBounds.width * outputScale;
    const drawHeight = sourceBounds.height * outputScale;
    const drawX = (CONFIG.outputSize - drawWidth) / 2;
    const drawY = (CONFIG.outputSize - drawHeight) / 2;

    outputContext.drawImage(
      image,
      sourceBounds.x,
      sourceBounds.y,
      sourceBounds.width,
      sourceBounds.height,
      drawX,
      drawY,
      drawWidth,
      drawHeight
    );

    const blob = await canvasToBlob(outputCanvas);
    return blob ? URL.createObjectURL(blob) : null;
  }

  function applyProcessedUrl(image, originalUrl, processedUrl) {
    if (!processedUrl || !image.isConnected) return;
    image.dataset.autoTrimOriginal = originalUrl;
    image.dataset.autoTrimProcessed = '1';
    if (image.hasAttribute('srcset')) {
      image.dataset.autoTrimSrcset = image.getAttribute('srcset');
      image.removeAttribute('srcset');
    }
    if (image.hasAttribute('data-src')) {
      image.dataset.autoTrimDataSrc = image.getAttribute('data-src');
      image.removeAttribute('data-src');
    }
    if (image.hasAttribute('data-srcset')) {
      image.dataset.autoTrimDataSrcset = image.getAttribute('data-srcset');
      image.removeAttribute('data-srcset');
    }
    image.src = processedUrl;
  }

  async function trimImage(image) {
    if (!image || image.dataset.autoTrimProcessed === '1') return;
    const originalUrl = image.currentSrc || image.src;
    if (!originalUrl || originalUrl.indexOf('blob:') === 0) return;

    try {
      if (!processedImages.has(originalUrl)) {
        processedImages.set(originalUrl, createTrimmedUrl(originalUrl));
      }
      const processedUrl = await processedImages.get(originalUrl);
      applyProcessedUrl(image, originalUrl, processedUrl);
      log(processedUrl ? 'Processed' : 'Original preserved', originalUrl);
    } catch (error) {
      processedImages.delete(originalUrl);
      console.warn('[AUTO-TRIM] Error processing image:', error);
    }
  }

  function processNextImage() {
    if (workQueueRunning || !workQueue.length) return;
    workQueueRunning = true;
    const image = workQueue.shift();

    schedule(function () {
      Promise.resolve(trimImage(image)).then(function () {
        workQueueRunning = false;
        processNextImage();
      }, function () {
        workQueueRunning = false;
        processNextImage();
      });
    });
  }

  function addToWorkQueue(image) {
    workQueue.push(image);
    processNextImage();
  }

  function unveilLazyImage(image) {
    if (window.lazySizes && window.lazySizes.loader) {
      window.lazySizes.loader.unveil(image);
      return;
    }

    let source = image.getAttribute('data-src');
    if (!source) return;
    const renderedWidth = Math.ceil(image.getBoundingClientRect().width || 400);
    const requestedWidth = Math.min(1200, Math.max(400, renderedWidth * window.devicePixelRatio));
    source = source.replace('{width}', requestedWidth);
    image.addEventListener('load', function () { addToWorkQueue(image); }, { once: true });
    image.classList.remove('lazyload', 'lazyloading');
    image.classList.add('lazyloaded');
    image.src = source;
  }

  function queueImage(image) {
    if (!image || image.dataset.autoTrimProcessed === '1' || queuedImages.has(image)) return;
    queuedImages.add(image);

    const run = function () {
      addToWorkQueue(image);
    };

    if (image.classList.contains('lazyload') || image.classList.contains('lazyloading')) {
      image.addEventListener('lazyloaded', run, { once: true });
      if (lazyImageObserver) {
        lazyImageObserver.observe(image);
      } else {
        unveilLazyImage(image);
      }
      return;
    }
    if (image.complete) {
      run();
    } else {
      image.addEventListener('load', run, { once: true });
    }
  }

  function processImages(root) {
    const scope = root && root.querySelectorAll ? root : document;
    if (scope.matches && scope.matches('img[data-auto-trim-image]')) {
      queueImage(scope);
    }
    scope.querySelectorAll('img[data-auto-trim-image]').forEach(queueImage);
  }

  function initialize() {
    processImages(document);

    const observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        mutation.addedNodes.forEach(function (node) {
          if (node.nodeType === 1) processImages(node);
        });
      });
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', function (event) {
    processImages(event.target);
  });
})();
